/**
 * The marks on the launcher.
 *
 * Drawn here, and that is a choice twice over.
 *
 * It avoids a dependency, in a product that carries two on purpose — but that is
 * the weaker half of the argument, because an icon package is small and
 * tree-shakeable and nobody would notice it.
 *
 * The stronger half: a general icon set does not have these objects in it. A gear
 * for administration and a bar chart for reports are the same gear and the same
 * bar chart every product uses, and they say nothing about this one. A hall board
 * is a panel on a bracket. A kiosk is a machine standing on the floor with a slot
 * where the paper comes out. A counter is a window with somebody behind it. Those
 * are the things in a branch, and a person who works in a branch recognises them
 * faster than they read a label.
 *
 * All eight are built the same way, because a set that is not consistent reads as
 * clip art: a 24-unit grid, strokes only, 1.6 units wide, round caps and joins, and
 * `currentColor` so a mark inherits the tile it sits on and works on the inverted
 * lead tile without a second version.
 */

type IconProps = { size?: number }

function Mark({ size = 22, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** A service window: the hatch, its ledge, and somebody behind it.
 *
 *  The first version put a head and shoulders above a bare line, which at 22px
 *  read as a person standing at a table rather than a counter. The frame is what
 *  makes it a window. */
export function CounterIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <path d="M3.5 19.5V7a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v12.5" />
      <path d="M2.5 19.5h19" />
      <path d="M3.5 15.5h17" />
      <circle cx="12" cy="9.2" r="2.1" />
      <path d="M8.6 15.5a3.4 3.4 0 0 1 6.8 0" />
    </Mark>
  )
}

/** A ticket, torn off and handed over: the notch is what makes it a ticket
 *  rather than a card. */
export function ReceptionIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <path d="M3 8.5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2.5 2.5 0 0 0 0 5v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2.5 2.5 0 0 0 0-5Z" />
      <path d="M9 10.5h6M9 13.5h3.5" />
    </Mark>
  )
}

/** A machine standing on the floor, with the slot the paper comes out of.
 *
 *  Narrow and tall on a foot, because the first version was a wide rounded
 *  rectangle full of lines and read as a document at small sizes. The slot is the
 *  detail that says which machine this is. */
export function KioskIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <rect x="6.5" y="2.6" width="11" height="13" rx="1.8" />
      <path d="M9.4 6.4h5.2M9.4 9.2h5.2" />
      <path d="M9.2 12.6h5.6" />
      <path d="M12 15.6v3.9M8 19.5h8" />
    </Mark>
  )
}

/** A panel on a bracket, high on a wall, with a number on it. */
export function BoardIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <rect x="2.8" y="4" width="18.4" height="11" rx="2" />
      <path d="M12 15v3.5M8.5 21h7" />
      <path d="M7.5 8.5h4M7.5 11h6" />
    </Mark>
  )
}

/** Bars at different heights. The only one of the eight that is the obvious
 *  glyph, because a chart of a chart is what a chart looks like. */
export function ReportsIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <path d="M3.5 20h17" />
      <path d="M7 20v-7M12 20v-12.5M17 20v-9.5" />
    </Mark>
  )
}

/** Two people, one behind the other: a list of them rather than one of them. */
export function PeopleIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <circle cx="9.5" cy="8" r="3.2" />
      <path d="M3.5 19.5a6 6 0 0 1 12 0" />
      <path d="M16.5 5.2a3.2 3.2 0 0 1 0 5.6" />
      <path d="M17.5 14.2a6 6 0 0 1 3 5.3" />
    </Mark>
  )
}

/** A frame with something being laid out inside it: the canvas editor, not a
 *  monitor. */
export function ScreensIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <rect x="6" y="7" width="6" height="4.5" rx="1" />
      <path d="M15 7.5h3M15 10.5h3M6 15h12M6 17.5h7" />
    </Mark>
  )
}

/** Sliders. Configuration is a set of things each set to a position, which is
 *  what a branch's catalogue actually is, and it is not a gear. */
export function AdministrationIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <path d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h8M16 17h4" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="14" cy="17" r="2" />
    </Mark>
  )
}

/** The record: a stack of entries with a seal on it. A trail's claim is that it
 *  cannot be quietly changed, so the mark is a document that has been closed. */
export function AuditIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <path d="M5.5 3.5h9a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
      <path d="M7.2 8h6.6" />
      <path d="M7.2 11.5h4.2" />
      <circle cx="16.8" cy="16.2" r="3.3" />
      <path d="m15.4 16.2 1 1 2-2.1" />
    </Mark>
  )
}

/** The floor from above: a row of windows, and people standing in front of them. */
export function FloorIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <path d="M3 5.5h18" />
      <path d="M4.5 5.5v4h4v-4M10 5.5v4h4v-4M15.5 5.5v4h4v-4" />
      <circle cx="7" cy="15.5" r="1.6" />
      <circle cx="12" cy="18" r="1.6" />
      <circle cx="17" cy="15" r="1.6" />
    </Mark>
  )
}

/** A day on the calendar with a mark on it: somebody is expected. */
export function BookingIcon(p: IconProps) {
  return (
    <Mark {...p}>
      <rect x="4" y="5.5" width="16" height="14" rx="1.5" />
      <path d="M4 9.5h16M8.5 3.5v4M15.5 3.5v4" />
      <path d="M9 14.5l2 2 4-4" />
    </Mark>
  )
}

// Line marks for the staff screens' design: 24px grid, 1.75 stroke,
// currentColor. The workspace marks match the main menu's.
const PATHS = {
  counter: 'M3 20h18M5 20V9h14v11M5 13h14M9 13v-2.5a3 3 0 0 1 6 0V13M12 4v2',
  reception: 'M7 3h10v18l-2-1.5-1.5 1.5-1.5-1.5-1.5 1.5L9 19.5 7 21zM10 8h4M10 12h4',
  floor: 'M3 4h18v12H3zM8 20h8M12 16v4M7 12l3-3 3 2 4-4',
  booking: 'M4 6h16v14H4zM4 10h16M8 3v5M16 3v5M9 15l2 2 4-4',
  reports: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  audit: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h5',
  people: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20c.8-3.5 3.3-5.5 6.5-5.5s5.7 2 6.5 5.5M16 4.3a3.5 3.5 0 0 1 0 6.4M18 14.8c1.8.8 3 2.6 3.5 5.2',
  designs: 'M3 4h18v13H3zM3 13h18M9 21h6M12 17v4M7 8h4',
  // KioskIcon's machine on its foot, as one path.
  machine: 'M8.3 2.6h7.4a1.8 1.8 0 0 1 1.8 1.8v9.4a1.8 1.8 0 0 1-1.8 1.8H8.3a1.8 1.8 0 0 1-1.8-1.8V4.4a1.8 1.8 0 0 1 1.8-1.8zM9.4 6.4h5.2M9.4 9.2h5.2M9.2 12.6h5.6M12 15.6v3.9M8 19.5h8',
  organisations: 'M3 21h18M5 21V4h9v17M14 9h5v12M8 8h3M8 12h3M8 16h3M16.5 13h.01M16.5 17h.01',
  admin: 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4',
  chevron: 'M6 9l6 6 6-6',
  close: 'M6 6l12 12M18 6L6 18',
  plus: 'M12 5v14M5 12h14',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.7 3.5 5.7 3.5 9s-1 6.3-3.5 9c-2.5-2.7-3.5-5.7-3.5-9s1-6.3 3.5-9z',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  undo: 'M9 7L4 12l5 5M4 12h11a5 5 0 0 1 0 10h-2',
  up: 'M6 15l6-6 6 6',
  down: 'M6 9l6 6 6-6',
  left: 'M15 6l-6 6 6 6',
  right: 'M9 6l6 6-6 6',
  sun: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon: 'M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  // A sheet of rows and columns: a spreadsheet file (CSV, Excel).
  table: 'M4 4h16v16H4zM4 9.5h16M4 15h16M10 4v16',
  // A page with its corner turned: a document file (PDF).
  document: 'M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6',
  upload: 'M12 20V9M7 14l5-5 5 5M5 4h14',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  pin: 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  alert: 'M12 3l10 18H2zM12 10v5M12 18h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 11v6M12 7.5h.01',
  pause: 'M9 5v14M15 5v14',
  play: 'M7 4l13 8-13 8z',
  phone: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  filter: 'M4 5h16l-6 7v6l-4 2v-8z',
  drag: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  edit: 'M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  refresh: 'M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5',
  user: 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c1-4 4-6 8-6s7 2 8 6',
  lock: 'M6 11h12v10H6zM8 11V7a4 4 0 0 1 8 0v4',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  eyeOff: 'M3 3l18 18M10.6 5.1A9.7 9.7 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3 3.9M6.6 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7c1.9 0 3.5-.6 4.9-1.4M9.9 9.9a3 3 0 0 0 4.2 4.2',
  signout: 'M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  arrowUpRight: 'M7 17L17 7M8 7h9v9',
  // Counter actions: call in, a walk-in, a no-show, a transfer, End.
  callIn: 'M4 12h11M11 7l5 5-5 5M20 5v14',
  userPlus: 'M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 21c1-4 3.5-6 7-6 1.5 0 2.8.4 3.9 1.1M18 14v6M15 17h6',
  userX: 'M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 21c1-4 3.5-6 7-6 1.5 0 2.8.4 3.9 1.1M15.5 15.5l5 5M20.5 15.5l-5 5',
  swap: 'M4 8h15l-4-4M20 16H5l4 4',
  stop: 'M6.5 6.5h11v11h-11z',
  // Settings menu marks.
  key: 'M8 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM11 11h10M18 11v3M15 11v2',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 8.5h.01',
  layers: 'M12 3l9 5-9 5-9-5zM3 13l9 5 9-5',
  plug: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0zM12 17v4',
  message: 'M4 5h16v11H9l-5 4zM8 9.5h8M8 12.5h5',
  shield: 'M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6zM9 12l2 2 4-4',
} as const

export type IconName = keyof typeof PATHS

export function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
